import React, { createContext, useContext, useState, useEffect } from 'react';
import { getTimeBasedTheme } from '../utils/timeUtils';

const ThemeContext = createContext();

const THEME_STORAGE_KEY = 'job-aggregator-theme';

export const themes = {
    morning: {
        name: 'Light Mode',
        colors: {
            bg: '#f8fafc',
            cardBg: '#ffffff',
            text: '#0f172a',
            textSecondary: '#64748b',
            accent: '#6366f1',
            accentLight: '#eef2ff',
            border: '#e2e8f0',
        },
        gradient: 'from-sky-50 via-indigo-50 to-blue-50',
    },
    night: {
        name: 'Dark Mode',
        colors: {
            bg: '#0f172a',
            cardBg: '#1e293b',
            text: '#f8fafc', // Made brighter white for better contrast
            textSecondary: '#cbd5e1', // Made brighter gray
            accent: '#8b5cf6',
            accentLight: '#4c1d95',
            border: '#334155',
        },
        gradient: 'from-slate-900 via-purple-900 to-slate-900',
    },
};

export const ThemeProvider = ({ children }) => {
    const [currentTheme, setCurrentTheme] = useState('morning');
    const [isAuto, setIsAuto] = useState(true);

    // Load theme preference on mount
    useEffect(() => {
        const saved = localStorage.getItem(THEME_STORAGE_KEY);
        if (saved) {
            try {
                const { theme, auto } = JSON.parse(saved);
                setCurrentTheme(theme);
                setIsAuto(auto);
            } catch (e) {
                console.error('Failed to parse theme preference', e);
            }
        }
    }, []);

    // Auto-update theme based on time
    useEffect(() => {
        if (!isAuto) return;

        const updateTheme = () => {
            const timeTheme = getTimeBasedTheme();
            setCurrentTheme(timeTheme);
        };

        updateTheme();
        const interval = setInterval(updateTheme, 60000); // Check every minute

        return () => clearInterval(interval);
    }, [isAuto]);

    // Apply theme to document
    useEffect(() => {
        // Fallback to morning theme if currentTheme is invalid or undefined
        const theme = themes[currentTheme] || themes.morning;

        // Safety check - if theme is somehow still undefined, don't proceed
        if (!theme) return;

        const root = document.documentElement;
        const body = document.body;

        // Apply dark class for night mode
        if (currentTheme === 'night') {
            root.classList.add('dark');
        } else {
            root.classList.remove('dark');
        }

        // Apply theme CSS variables
        if (theme.colors) {
            Object.entries(theme.colors).forEach(([key, value]) => {
                root.style.setProperty(`--theme-${key}`, value);
            });

            // Update body styles directly
            body.style.backgroundColor = theme.colors.bg;
            body.style.color = theme.colors.text;

            // Update meta theme-color for mobile browsers
            const metaTheme = document.querySelector('meta[name="theme-color"]');
            if (metaTheme) {
                metaTheme.setAttribute('content', theme.colors.bg);
            }
        }
    }, [currentTheme]);

    const setTheme = (themeName, auto = false) => {
        setCurrentTheme(themeName);
        setIsAuto(auto);
        localStorage.setItem(THEME_STORAGE_KEY, JSON.stringify({ theme: themeName, auto }));
    };

    const toggleAuto = () => {
        const newAuto = !isAuto;
        setIsAuto(newAuto);

        if (newAuto) {
            const timeTheme = getTimeBasedTheme();
            setCurrentTheme(timeTheme);
            localStorage.setItem(THEME_STORAGE_KEY, JSON.stringify({ theme: timeTheme, auto: true }));
        } else {
            localStorage.setItem(THEME_STORAGE_KEY, JSON.stringify({ theme: currentTheme, auto: false }));
        }
    };

    const value = {
        currentTheme,
        theme: themes[currentTheme],
        isAuto,
        setTheme,
        toggleAuto,
        themes,
    };

    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within ThemeProvider');
    }
    return context;
};
